import React, { FunctionComponent, useCallback } from 'react';
import styled from 'styled-components';
import { useMutation } from 'react-query';
import { SubscriptionI, SubscriptionStatusE } from '@/types/cyclone/models';
import { Card } from '.';
import { clipboardGray } from '@/static/images';
import { useNavigate } from 'react-router-dom';
import { trackGenericEvent } from '@/analytics';
import { useClient } from '@/hooks';
import { useConfirmation } from '@/contexts';
import { ErrorI } from '@/types/cyclone/requests';
import { toast } from 'react-toastify';

type SubscriptionsProps = {
  subscriptions: SubscriptionI[];
  refetch: () => void;
};

type EmptyObject = {
  [K in string]: never;
};

export const Subscriptions: FunctionComponent<SubscriptionsProps> = ({ subscriptions, refetch }) => {
  const navigate = useNavigate();
  const { client } = useClient();
  const { confirm } = useConfirmation();

  // Memoize the refetch function to prevent unnecessary re-renders of Card components
  const memoizedRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  // Status change mutation
  const statusMutation = useMutation<EmptyObject, ErrorI, { id: string; status: SubscriptionStatusE }>(
    ({ id, status }) =>
      client(`subscriptions/${id}/status`, 'PUT', {
        isAuthRequired: true,
        data: { status }
      }),
    {
      onSuccess: () => {
        memoizedRefetch();
      }
    }
  );

  // Delete mutation
  const deleteMutation = useMutation<EmptyObject, ErrorI, string>(
    (id) =>
      client(`subscriptions/${id}`, 'DELETE', {
        isAuthRequired: true
      }),
    {
      onSuccess: () => {
        memoizedRefetch();
        toast.success('Se ha eliminado exitosamente');
      },
      onError: (e: any) => {
        if (e.code && e.code === 'G-001') {
          toast.error('Este plan mensual tiene usuarios activos');
        } else {
          toast.error('Algo anda mal. Por favor, contactar a soporte.');
        }
      }
    }
  );

  // Handle status change
  const handleStatusChange = useCallback(
    (id: string, currentStatus: SubscriptionStatusE) => {
      const newStatus =
        currentStatus === SubscriptionStatusE.ACTIVE
          ? SubscriptionStatusE.PAUSED
          : SubscriptionStatusE.ACTIVE;
      statusMutation.mutate({ id, status: newStatus });
    },
    [statusMutation]
  );

  // Handle delete
  const handleDelete = useCallback(
    (id: string) => {
      deleteMutation.mutate(id);
    },
    [deleteMutation]
  );

  // Confirm status change
  const confirmStatusChange = useCallback(
    (id: string, status: SubscriptionStatusE) => {
      if (status === SubscriptionStatusE.ACTIVE) {
        return confirm({
          status: 'warning',
          content: '¿Cambiar el estado a este plan mensual?',
          title: 'Archivar plan mensual',
          confirmText: 'Si, pausar',
          onConfirm: () => handleStatusChange(id, status)
        });
      } else {
        handleStatusChange(id, status);
      }
    },
    [confirm, handleStatusChange]
  );

  // Confirm delete
  const confirmDelete = useCallback(
    (id: string) =>
      confirm({
        status: 'danger',
        content: '¿Querés eliminar este plan mensual?',
        title: 'Eliminar plan mensual',
        confirmText: 'Si, eliminar',
        onConfirm: () => handleDelete(id)
      }),
    [confirm, handleDelete]
  );

  if (!subscriptions.length)
    return (
      <EmptyContainer>
        <ImageIcon src={clipboardGray} />
        <EmptyText>No tenés planes mensuales añadidos</EmptyText>
        <Link
          onClick={() => {
            trackGenericEvent('Button Create New Asset Clicked', { type: 'Subscription' }),
              navigate('/planesmensuales/crear');
          }}
        >
          Agregar nuevo
        </Link>
      </EmptyContainer>
    );

  return (
    <Container>
      {subscriptions.map((subscription) => (
        <Card
          subscription={subscription}
          key={subscription.id}
          onStatusChange={() => confirmStatusChange(subscription.id.toString(), subscription.status)}
          onDelete={() => confirmDelete(subscription.id.toString())}
        />
      ))}
    </Container>
  );
};

const Container = styled.div`
  display: grid;
  grid-row-gap: 1rem;
  grid-column-gap: 3rem;
  grid-template-columns: repeat(auto-fill, minmax(290px, 290px));
`;

const EmptyContainer = styled.div`
  padding: 70px;
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;

  ${({ theme }) => theme.breakpoint('md')} {
    padding: 50px;
  }
`;

const EmptyText = styled.span`
  font-size: 1.125rem;
  padding-top: 35px;
`;

const Link = styled.a`
  color: ${({ theme }) => theme.colors.blue[500]};
  padding-top: 20px;
  font-size: 1.125rem;
  cursor: pointer;
`;

const ImageIcon = styled.img`
  width: 100px;
  height: 110px;
`;
